import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import api from '../../services/api';
import SABadge from '../../components/SuperAdmin/SABadge';
import '../../assets/styles/SuperAdmin/SATheme.css';
import '../../assets/styles/SuperAdmin/SAUserMgmt.css';

const InlineMsg = ({ msg, type }) => {
    if (!msg) return null;
    const bg = type === 'error' ? '#ff5252' : '#2563eb';
    return (
        <div style={{
            background: bg + '18', border: `1px solid ${bg}40`,
            color: bg, borderRadius: 8, padding: '8px 14px',
            fontSize: 12, fontWeight: 600, marginBottom: 14
        }}>{msg}</div>
    );
};

const CreateUserModal = ({ onClose, onSuccess, SA_ROLES }) => {
    const [form, setForm] = useState({ name: '', role: 'employee', email: '', id: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState(null);

    const handleCreate = async () => {
        if (!form.name.trim()) return setMsg({ text: 'Full name is required', type: 'error' });
        if (!form.id.trim()) return setMsg({ text: 'Employee ID is required', type: 'error' });
        if (!form.password.trim()) return setMsg({ text: 'Password is required', type: 'error' });

        setLoading(true);
        setMsg(null);
        try {
            await api.createUser({
                empId: form.id.trim(),
                name: form.name.trim(),
                email: form.email.trim() || form.id.trim().toLowerCase().replace(/[^a-z0-9]/g, '') + '@amshrms.in',
                role: form.role,
                password: form.password.trim()
            });
            setMsg({ text: '✅ User created successfully!', type: 'success' });
            setTimeout(() => { onSuccess(); onClose(); }, 1000);
        } catch (e) {
            setMsg({ text: e.message || 'Failed to create user', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return ReactDOM.createPortal(
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(6,8,12,0.88)',
                backdropFilter: 'blur(8px)',
                zIndex: 99999,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: 'var(--sa-card)',
                    border: '1px solid var(--sa-border)',
                    borderRadius: 18, padding: 28,
                    width: 480, maxWidth: '94vw',
                    color: 'var(--sa-text)',
                    boxShadow: '0 32px 100px rgba(0,0,0,0.7)'
                }}
            >
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6, fontFamily: "'Sora'" }}>Create User</div>
                <div style={{ fontSize: 12, color: 'var(--sa-muted)', marginBottom: 20 }}>Create a new user account</div>

                <InlineMsg msg={msg?.text} type={msg?.type} />

                <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sa-muted)', marginBottom: 4 }}>FULL NAME *</div>
                    <input
                        autoFocus
                        value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g. John Doe"
                        className="sa-cred-input"
                    />
                </div>

                <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sa-muted)', marginBottom: 4 }}>ROLE *</div>
                    <select
                        value={form.role}
                        onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                        className="sa-cred-input"
                        style={{ cursor: 'pointer' }}
                    >
                        {SA_ROLES.filter(r => r.id !== 'superadmin').map(r => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                    </select>
                </div>

                <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sa-muted)', marginBottom: 4 }}>EMAIL (optional)</div>
                    <input
                        value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="e.g. john@amshrms.in"
                        className="sa-cred-input"
                        type="email"
                    />
                </div>

                <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sa-muted)', marginBottom: 4 }}>EMPLOYEE ID *</div>
                    <input
                        value={form.id}
                        onChange={e => setForm(f => ({ ...f, id: e.target.value }))}
                        placeholder="e.g. EMP-2026-0001"
                        className="sa-cred-input"
                    />
                </div>

                <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sa-muted)', marginBottom: 4 }}>PASSWORD *</div>
                    <input
                        value={form.password}
                        onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                        placeholder="Enter password"
                        className="sa-cred-input"
                        type="text"
                    />
                </div>

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} className="sa-action-btn" disabled={loading}>Cancel</button>
                    <button
                        onClick={handleCreate}
                        disabled={loading}
                        style={{
                            background: loading ? 'var(--sa-dim)' : 'var(--sa-accent)',
                            color: '#fff', border: 'none',
                            padding: '9px 24px', borderRadius: 8,
                            fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: 13, transition: 'all 0.2s'
                        }}
                    >
                        {loading ? 'Creating...' : '💾 Save'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const SAUserMgmt = ({ users, SA_ROLES, onUpdateUser, onResetPass, onRefresh }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRole, setSelectedRole] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);

    const adminRoles = ['hr', 'pm', 'itadmin', 'superadmin'];
    
    const filteredUsers = users.filter(u => {
        const matchesSearch = !searchTerm || 
            u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            u.emp_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesRole = selectedRole === 'all' || u.role === selectedRole;
        return matchesSearch && matchesRole;
    });

    const normalizeStatus = (status) => {
        if (!status) return 'active';
        const s = status.toLowerCase();
        return s === 'inactive' || s === 'inactive' ? 'inactive' : 'active';
    };

    return (
        <div className="animate-in">
            {/* Fixed Header */}
            <div style={{ 
                position: 'sticky', 
                top: 0, 
                zIndex: 100, 
                background: 'var(--sa-bg)', 
                paddingBottom: 20,
                marginBottom: 20,
                borderBottom: '1px solid var(--sa-border)'
            }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, fontFamily: "'Sora'" }}>User Governance</h2>
                        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--sa-muted)" }}>Search, block, or modify user roles across the organization</p>
                    </div>
                    <button 
                        onClick={() => setShowCreateModal(true)}
                        style={{ background: "var(--sa-teal)", color: "#fff", border: "none", padding: "8px 20px", borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans'" }}
                    >
                        + Create User
                    </button>
                </div>
                
                {/* Role Filters */}
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "var(--sa-muted)", textTransform: "uppercase" }}>Filter by Role:</span>
                    <button
                        onClick={() => setSelectedRole('all')}
                        style={{
                            background: selectedRole === 'all' ? 'var(--sa-accent)' : 'var(--sa-card)',
                            color: selectedRole === 'all' ? '#fff' : 'var(--sa-text)',
                            border: '1px solid var(--sa-border)',
                            padding: '6px 14px',
                            borderRadius: 8,
                            fontWeight: 700,
                            fontSize: 11,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    >
                        All ({users.length})
                    </button>
                    {SA_ROLES.filter(r => r.id !== 'superadmin').map(r => {
                        const count = users.filter(u => u.role === r.id).length;
                        return (
                            <button
                                key={r.id}
                                onClick={() => setSelectedRole(r.id)}
                                style={{
                                    background: selectedRole === r.id ? r.color : 'var(--sa-card)',
                                    color: selectedRole === r.id ? '#fff' : 'var(--sa-text)',
                                    border: `1px solid ${selectedRole === r.id ? r.color : 'var(--sa-border)'}`,
                                    padding: '6px 14px',
                                    borderRadius: 8,
                                    fontWeight: 700,
                                    fontSize: 11,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {r.name} ({count})
                            </button>
                        );
                    })}
                </div>

                {/* Search */}
                <input 
                    type="text" 
                    placeholder="Search by name, ID or email..." 
                    className="sa-user-search"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Scrollable Table */}
            <div className="sa-user-table-card" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                <table className="sa-table">
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--sa-card)', zIndex: 10 }}>
                        <tr>
                            <th>User</th>
                            <th>Employee ID</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length === 0 ? (
                            <tr>
                                <td colSpan={5} style={{ textAlign: 'center', color: 'var(--sa-muted)', padding: '32px 0', fontSize: 13 }}>
                                    No users found
                                </td>
                            </tr>
                        ) : filteredUsers.map(u => {
                            const r = SA_ROLES.find(x => x.id === u.role) || { color: "var(--sa-dim)", name: u.role };
                            const isActive = normalizeStatus(u.status) === 'active';
                            const isAdmin = adminRoles.includes(u.role);
                            return (
                                <tr key={u.id || u.emp_id}>
                                    <td>
                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <div style={{ width: 32, height: 32, borderRadius: 8, background: r.color + "18", color: r.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800 }}>
                                                {u.name ? u.name.substring(0, 2).toUpperCase() : 'U'}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 700 }}>{u.name}</div>
                                                <div style={{ fontSize: 11, color: "var(--sa-muted)" }}>{u.email || '—'}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td><code style={{ background: "var(--sa-bg)", padding: "2px 6px", borderRadius: 4, color: "var(--sa-orange)", fontSize: 11 }}>{u.emp_id}</code></td>
                                    <td><SABadge color={r.color === "var(--sa-red)" ? "red" : r.color === "var(--sa-teal)" ? "teal" : "blue"}>{r.name}</SABadge></td>
                                    <td><SABadge color={isActive ? "green" : "red"} dot>{isActive ? "Active" : "Inactive"}</SABadge></td>
                                    <td>
                                        <div style={{ display: "flex", gap: 6 }}>
                                            <button onClick={() => onUpdateUser(u.id || u.emp_id, 'role')} className="sa-action-btn" style={{ color: "var(--sa-blue)", borderColor: "rgba(77,168,255,0.2)" }}>Edit</button>
                                            <button onClick={() => onResetPass(u.id || u.emp_id)} className="sa-action-btn" style={{ color: "var(--sa-orange)", borderColor: "rgba(253,171,61,0.2)" }}>Reset</button>
                                            {!isAdmin && (
                                                <button 
                                                    onClick={() => onUpdateUser(u.id || u.emp_id, isActive ? 'block' : 'unblock')} 
                                                    className="sa-action-btn" 
                                                    style={{ color: isActive ? "var(--sa-red)" : "var(--sa-green)", borderColor: isActive ? "rgba(255,107,107,0.2)" : "rgba(0,214,143,0.2)" }}
                                                >
                                                    {isActive ? "Block" : "Unblock"}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showCreateModal && (
                <CreateUserModal
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={onRefresh}
                    SA_ROLES={SA_ROLES}
                />
            )}
        </div>
    );
};

export default SAUserMgmt;
